
import type React from "react"

import { useState, useEffect, useMemo, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { motion, AnimatePresence } from "motion/react"
import {
  AlertTriangle,
  Brush01,
  ChevronDown,
  Package,
  RefreshCw01,
  Scale02,
  Tool01,
} from "@untitledui/icons"

import { useOrganization } from "@/lib/organization/organization-context"
import { teamsApi } from "@/lib/api/teams"
import { useTeam } from "@/lib/api/use-teams"
import { queryKeys } from "@/lib/api/query-keys"
import { ApiError } from "@/lib/api/client"
import type { TeamSettingsMetadata } from "@/lib/api/types"
import { BODYSHOP_PLACEHOLDER_METADATA } from "@/lib/fees/defaults"
import { cx } from "@/lib/utils/cx"

// Mirrors PUT /v1/teams/{teamId}/settings → metadata block: 6 labor + 2
// materials + 2 storage + 2 scan = 12 numbers. Backend seeds non-zero
// placeholder defaults on team creation (BODYSHOP_DEFAULT_METADATA on the
// entity), so this surface is a "review and tune" form, not a "fill from
// scratch" form. Inputs show a `placeholder $X` chip; saving overwrites the
// placeholder with the user's real rate.

type LaborField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.laborRate
type MaterialsField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.materials
type ExtraFeesField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.extraFees
type VehicleScansField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.vehicleScans

type SectionKey = "laborRate" | "materials" | "extraFees" | "vehicleScans"

const LABOR_LABELS: Record<LaborField, string> = {
  bodyLabor: "Body labor",
  paintLabor: "Paint labor",
  frameLabor: "Frame labor",
  mechanical: "Mechanical",
  aluminumBody: "Aluminum body",
  aluminumFrame: "Aluminum frame",
}
const MATERIALS_LABELS: Record<MaterialsField, string> = {
  bodyMaterials: "Body materials",
  paintMaterials: "Paint materials",
}
const EXTRA_FEES_LABELS: Record<ExtraFeesField, string> = {
  insideStorage: "Inside storage",
  outsideStorage: "Outside storage",
}
const SCAN_LABELS: Record<VehicleScansField, string> = {
  preRepairScan: "Pre-repair scan",
  postRepairScan: "Post-repair scan",
}

// Form values are stored as strings, not numbers. With number-backed state,
// `<input type="number">` re-rendered an empty string as "0" the moment the
// user backspaced (NaN → fallback to 0 → controlled value renders "0"),
// forcing a second backspace to actually clear the field. String state +
// `inputMode="decimal"` gives a clean numeric keypad on mobile, no
// browser-native number-input quirks, and lets the field be genuinely empty
// while the user is mid-edit. Strings are parsed to numbers only when
// saving.
interface FormState {
  laborRate: Record<LaborField, string>
  materials: Record<MaterialsField, string>
  extraFees: Record<ExtraFeesField, string>
  vehicleScans: Record<VehicleScansField, string>
}

function placeholderAsStrings(): FormState {
  const numToStr = <T extends Record<string, number>>(o: T): Record<keyof T, string> => {
    const out = {} as Record<keyof T, string>
    for (const k in o) out[k] = String(o[k])
    return out
  }
  return {
    laborRate: numToStr(BODYSHOP_PLACEHOLDER_METADATA.laborRate),
    materials: numToStr(BODYSHOP_PLACEHOLDER_METADATA.materials),
    extraFees: numToStr(BODYSHOP_PLACEHOLDER_METADATA.extraFees),
    vehicleScans: numToStr(BODYSHOP_PLACEHOLDER_METADATA.vehicleScans),
  }
}

const PLACEHOLDER_FORM: FormState = placeholderAsStrings()

function parseRate(v: string): number {
  if (!v.trim()) return 0
  const n = Number.parseFloat(v)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

interface SectionMeta {
  key: SectionKey
  title: string
  unit: string
  icon: typeof Tool01
  tone: "amber" | "emerald" | "indigo" | "sky"
  entries: [string, string][]
  defaults: Record<string, number>
}

const SECTIONS: SectionMeta[] = [
  {
    key: "laborRate",
    title: "Labor rates",
    unit: "$ / hour",
    icon: Tool01,
    tone: "amber",
    entries: Object.entries(LABOR_LABELS),
    defaults: BODYSHOP_PLACEHOLDER_METADATA.laborRate,
  },
  {
    key: "materials",
    title: "Materials",
    unit: "$ / hour",
    icon: Brush01,
    tone: "emerald",
    entries: Object.entries(MATERIALS_LABELS),
    defaults: BODYSHOP_PLACEHOLDER_METADATA.materials,
  },
  {
    key: "extraFees",
    title: "Storage fees",
    unit: "$ / day",
    icon: Package,
    tone: "indigo",
    entries: Object.entries(EXTRA_FEES_LABELS),
    defaults: BODYSHOP_PLACEHOLDER_METADATA.extraFees,
  },
  {
    key: "vehicleScans",
    title: "Vehicle scans",
    unit: "Flat fee",
    icon: Scale02,
    tone: "sky",
    entries: Object.entries(SCAN_LABELS),
    defaults: BODYSHOP_PLACEHOLDER_METADATA.vehicleScans,
  },
]

const TONE_TILE: Record<SectionMeta["tone"], string> = {
  amber: "bg-gradient-to-br from-amber-500 to-amber-700 shadow-amber-600/25",
  emerald: "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-600/25",
  indigo: "bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-indigo-600/25",
  sky: "bg-gradient-to-br from-sky-500 to-sky-700 shadow-sky-600/25",
}

function isPlaceholder(value: string | undefined, defaultValue: number): boolean {
  if (value == null) return true
  // Empty string is treated as "still placeholder" (user hasn't entered
  // anything) — the parsed value would be 0, but visually nothing is set.
  if (!value.trim()) return true
  const n = Number.parseFloat(value)
  if (!Number.isFinite(n)) return true
  return n === defaultValue
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

interface FeesSettingsProps {
  teamId?: number | string | null
  // teamKind kept for parity with StoreSettings — empty-state copy uses it.
  teamKind?: "dealership" | "bodyshop" | "group"
}

export function FeesSettings({ teamId: teamIdProp }: FeesSettingsProps = {}) {
  const ctx = useOrganization()
  const teamId = teamIdProp ?? ctx.activeTeamId
  const activeTeamKind = ctx.activeTeamKind
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: team, isLoading } = useTeam(teamId)

  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<FormState>(PLACEHOLDER_FORM)
  const initialRef = useRef<FormState>(PLACEHOLDER_FORM)
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    laborRate: true,
    materials: false,
    extraFees: false,
    vehicleScans: false,
  })

  useEffect(() => {
    if (!team) return
    const meta = (team.metadata ?? {}) as TeamSettingsMetadata
    // Stringify numbers from the BE so the inputs (which are string-bound)
    // can edit them naturally. Fields the BE doesn't return inherit the
    // placeholder string.
    const merge = <T extends Record<string, number>>(
      defaults: Record<keyof T, string>,
      incoming: Partial<Record<keyof T, number>> | undefined,
    ): Record<keyof T, string> => {
      const out: Record<keyof T, string> = { ...defaults }
      if (!incoming) return out
      for (const k in incoming) {
        const v = incoming[k]
        if (typeof v === "number" && Number.isFinite(v)) {
          out[k as keyof T] = String(v)
        }
      }
      return out
    }
    const next: FormState = {
      laborRate: merge(PLACEHOLDER_FORM.laborRate, meta.laborRate),
      materials: merge(PLACEHOLDER_FORM.materials, meta.materials),
      extraFees: merge(PLACEHOLDER_FORM.extraFees, meta.extraFees),
      vehicleScans: merge(PLACEHOLDER_FORM.vehicleScans, meta.vehicleScans),
    }
    setFormData(next)
    initialRef.current = next
  }, [team])

  const sectionStats = useMemo(() => {
    return SECTIONS.map((sec) => {
      const values = formData[sec.key] as Record<string, string>
      let placeholder = 0
      for (const [field] of sec.entries) {
        if (isPlaceholder(values[field], sec.defaults[field])) placeholder++
      }
      return {
        key: sec.key,
        placeholder,
        total: sec.entries.length,
        customized: sec.entries.length - placeholder,
      }
    })
  }, [formData])

  const totalStats = useMemo(() => {
    return sectionStats.reduce(
      (acc, s) => ({
        placeholder: acc.placeholder + s.placeholder,
        total: acc.total + s.total,
      }),
      { placeholder: 0, total: 0 },
    )
  }, [sectionStats])

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialRef.current)
  }, [formData])

  const updateField = (section: SectionKey, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...(prev[section] as Record<string, string>), [field]: value },
    }))
  }

  const resetSection = (section: SectionKey) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...(PLACEHOLDER_FORM[section] as Record<string, string>) },
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!teamId || !team) return
    setSaving(true)
    try {
      // Parse strings → numbers at save time. Empty fields fall back to 0
      // (effectively "no charge for this line"). The user can use the
      // per-section reset to restore placeholder values if they cleared
      // by accident.
      const toNumbers = <T extends Record<string, string>>(
        section: T,
      ): Record<keyof T, number> => {
        const out = {} as Record<keyof T, number>
        for (const k in section) out[k] = parseRate(section[k])
        return out
      }
      await teamsApi.updateSettings(Number(teamId), {
        metadata: {
          laborRate: toNumbers(formData.laborRate),
          materials: toNumbers(formData.materials),
          extraFees: toNumbers(formData.extraFees),
          vehicleScans: toNumbers(formData.vehicleScans),
        },
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      initialRef.current = formData
      toast.success("Fees & rates updated")
      // Bounce back to the route the user came from (e.g. /estimates/quick
      // when redirected here by EstimatePrerequisiteGate). The gate sets
      // ?return=… on its primary CTA; we honor it after a successful save.
      const returnUrl = searchParams.get("return")
      if (returnUrl) navigate(returnUrl)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to update fees"
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (!teamId) {
    return (
      <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
        <p className="text-sm text-zinc-500">
          Select a {activeTeamKind === "bodyshop" ? "body shop" : "store"} from the team
          switcher to edit fees.
        </p>
      </div>
    )
  }

  const allOnPlaceholder = totalStats.placeholder === totalStats.total
  const noneOnPlaceholder = totalStats.placeholder === 0

  return (
    <div className="space-y-5">
      {/* Top summary card — colour-shifts with status */}
      <div
        className={cx(
          "rounded-2xl border shadow-sm overflow-hidden",
          allOnPlaceholder
            ? "border-amber-200/80 bg-amber-50/40"
            : noneOnPlaceholder
              ? "border-emerald-200/70 bg-emerald-50/40"
              : "border-zinc-200/60 bg-white",
        )}
      >
        <div className="px-6 py-5 flex items-start gap-4">
          <div
            className={cx(
              "size-11 rounded-2xl grid place-items-center shrink-0 shadow-md",
              allOnPlaceholder
                ? "bg-gradient-to-br from-amber-500 to-amber-700 shadow-amber-600/25"
                : noneOnPlaceholder
                  ? "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-600/25"
                  : "bg-zinc-900",
            )}
          >
            {allOnPlaceholder ? (
              <AlertTriangle className="size-5 text-white" aria-hidden />
            ) : (
              <Tool01 className="size-5 text-white" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold text-zinc-900">Fees & rates</h3>
            <p className="text-sm text-zinc-500 mt-0.5 leading-relaxed">
              {allOnPlaceholder ? (
                <>
                  All <span className="font-semibold text-amber-800">{totalStats.total} fields</span>{" "}
                  are still placeholder values. Set yours so estimates price for your real
                  shop, not industry stand-ins.
                </>
              ) : noneOnPlaceholder ? (
                <>
                  Every fee is set to your shop's real rates — estimates use these
                  numbers verbatim.
                </>
              ) : (
                <>
                  <span className="font-semibold text-zinc-900">
                    {totalStats.total - totalStats.placeholder} of {totalStats.total}
                  </span>{" "}
                  customized.{" "}
                  <span className="text-zinc-500">
                    {totalStats.placeholder} still on placeholder.
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Section cards */}
      <form onSubmit={handleSubmit}>
        {isLoading ? (
          <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
            <p className="text-sm text-zinc-400">Loading fees…</p>
          </div>
        ) : (
          <div className="space-y-4 pb-28">
            {SECTIONS.map((sec) => {
              const stats = sectionStats.find((s) => s.key === sec.key)!
              const Icon = sec.icon
              const isOpen = expanded[sec.key]
              const allPlaceholderHere = stats.placeholder === stats.total
              return (
                <div
                  key={sec.key}
                  className={cx(
                    "rounded-2xl border bg-white shadow-sm overflow-hidden",
                    isOpen ? "border-zinc-300/70" : "border-zinc-200/60",
                  )}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((e) => ({ ...e, [sec.key]: !e[sec.key] }))
                    }
                    className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-zinc-50/60 transition-colors duration-100"
                  >
                    <div
                      className={cx(
                        "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
                        TONE_TILE[sec.tone],
                      )}
                    >
                      <Icon className="size-5 text-white" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-[15px] font-semibold text-zinc-900">
                          {sec.title}
                        </h4>
                        <span
                          className={cx(
                            "inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide",
                            allPlaceholderHere
                              ? "bg-amber-100 text-amber-800"
                              : stats.placeholder === 0
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-zinc-100 text-zinc-600",
                          )}
                        >
                          {stats.customized} of {stats.total} customized
                        </span>
                      </div>
                      <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">
                        {sec.unit}
                        {!isOpen && stats.customized > 0 && (
                          <>
                            {" "}
                            ·{" "}
                            <span className="text-zinc-400">
                              {sec.entries
                                .filter(([f]) => {
                                  const v = (formData[sec.key] as Record<string, string>)[f]
                                  return !isPlaceholder(v, sec.defaults[f])
                                })
                                .slice(0, 3)
                                .map(([f, l]) => {
                                  const v = (formData[sec.key] as Record<string, string>)[f]
                                  return `${l.toLowerCase()} $${formatMoney(parseRate(v))}`
                                })
                                .join(" · ")}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="shrink-0 text-zinc-400"
                    >
                      <ChevronDown className="size-5" aria-hidden />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="px-5 pb-5 pt-1 border-t border-zinc-100 space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {sec.entries.map(([field, label]) => {
                              const value = (formData[sec.key] as Record<string, string>)[field] ?? ""
                              const defaultValue = sec.defaults[field]
                              const placeholder = isPlaceholder(value, defaultValue)
                              return (
                                <div key={field} className="space-y-1.5">
                                  <div className="flex items-center justify-between gap-2">
                                    <label
                                      htmlFor={`fee-${field}`}
                                      className="text-[13px] font-semibold text-zinc-700"
                                    >
                                      {label}
                                    </label>
                                    <span
                                      className={cx(
                                        "text-[10.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded",
                                        placeholder
                                          ? "bg-amber-50 text-amber-700"
                                          : "bg-zinc-100 text-zinc-500",
                                      )}
                                    >
                                      {placeholder
                                        ? `placeholder $${formatMoney(defaultValue)}`
                                        : `was $${formatMoney(defaultValue)}`}
                                    </span>
                                  </div>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-medium">
                                      $
                                    </span>
                                    {/* `type="text"` + `inputMode="decimal"` gets us the
                                        numeric keypad on mobile without browser-native
                                        number-input quirks (no leading-0 on backspace,
                                        no spinner buttons on desktop). The pattern
                                        regex blocks letters at the typing layer. */}
                                    <input
                                      id={`fee-${field}`}
                                      type="text"
                                      inputMode="decimal"
                                      pattern="[0-9]*\.?[0-9]*"
                                      placeholder={String(defaultValue)}
                                      value={value}
                                      onChange={(e) => {
                                        const v = e.target.value
                                        // Accept only digits + at most one decimal
                                        // point. Empty string is allowed.
                                        if (v === "" || /^[0-9]*\.?[0-9]*$/.test(v)) {
                                          updateField(sec.key, field, v)
                                        }
                                      }}
                                      onFocus={(e) => e.currentTarget.select()}
                                      className={cx(
                                        "w-full h-11 rounded-xl border bg-white pl-7 pr-3 text-[14px] text-zinc-900 placeholder:text-zinc-300 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10",
                                        placeholder ? "border-zinc-200" : "border-zinc-300",
                                      )}
                                    />
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                          <div className="flex justify-end pt-1">
                            <button
                              type="button"
                              onClick={() => resetSection(sec.key)}
                              disabled={stats.customized === 0}
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12.5px] font-semibold text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-zinc-500 transition-all duration-150"
                            >
                              <RefreshCw01 className="size-3.5" aria-hidden />
                              Reset section
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        )}

        {/* Sticky save bar — appears only when dirty */}
        <AnimatePresence>
          {isDirty && (
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:w-[420px] z-30"
            >
              <div className="rounded-2xl border border-zinc-200/70 bg-white/95 backdrop-blur-xl shadow-2xl shadow-zinc-900/15 px-5 py-3 flex items-center gap-3">
                <div className="size-9 rounded-xl bg-emerald-50 grid place-items-center shrink-0">
                  <Tool01 className="size-4 text-emerald-600" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-zinc-900">Unsaved changes</p>
                  <p className="text-[11.5px] text-zinc-500 truncate">
                    Save to apply your rates to future estimates.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="shrink-0 h-10 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold disabled:opacity-50 transition-colors duration-150 flex items-center gap-1.5"
                >
                  {saving ? (
                    <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    "Save changes"
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </div>
  )
}
